// =============================================================================
// GOUVERNANCE - Findings (collection canonique governance_findings)
// =============================================================================
// Lit la collection canonique `governance_findings` via les endpoints lecteurs :
//   GET /api/governance/findings/stats            — total + ventilations
//   GET /api/governance/findings?limit=50&...     — liste filtrable
//   GET /api/governance/findings/{reference}      — un finding (non utilisé ici)
// Scoping serveur : super_admin sans param => PLATEFORME (tous tenants) ;
//   ?tenant_id=X => scope ; non-super => forcé à son tenant.
// Lecture seule (pas de bulk actions). Charte OmniHub : tokens var(--oh-*) only.
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { getTenantHeaders } from "../../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Paires fond/texte dual-mode (contraste garanti by-design) par gravité.
// CRITIQUE=red · GRAVE=orange · MODERE=yellow · MINEUR=blue.
const GRAVITE_META = {
  CRITIQUE: { label: "Critique", bg: "var(--oh-stat-red-bg)", text: "var(--oh-stat-red-text)" },
  GRAVE: { label: "Grave", bg: "var(--oh-stat-orange-bg)", text: "var(--oh-stat-orange-text)" },
  MODERE: { label: "Modéré", bg: "var(--oh-stat-yellow-bg)", text: "var(--oh-stat-yellow-text)" },
  MINEUR: { label: "Mineur", bg: "var(--oh-stat-blue-bg)", text: "var(--oh-stat-blue-text)" },
};

const GRAVITE_ORDER = ["CRITIQUE", "GRAVE", "MODERE", "MINEUR"];

function graviteMeta(g) {
  const key = String(g || "").toUpperCase();
  return GRAVITE_META[key] || { label: g || "—", bg: "var(--oh-bg-surface)", text: "var(--oh-text-secondary)" };
}

// Badge gravité — paire fond/texte dual-mode.
function GraviteBadge({ gravite }) {
  const meta = graviteMeta(gravite);
  return (
    <span
      className="inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-medium"
      style={{ background: meta.bg, color: meta.text, borderRadius: "8px" }}
    >
      {meta.label}
    </span>
  );
}

// Badge statut — neutre (contour + texte secondaire), pas de sémantique gravité.
function StatutBadge({ statut }) {
  return (
    <span
      className="inline-flex items-center rounded-lg px-2 py-0.5 text-xs"
      style={{
        border: "0.5px solid var(--oh-border)",
        color: "var(--oh-text-secondary)",
        borderRadius: "8px",
      }}
    >
      {statut || "—"}
    </span>
  );
}

function StatTile({ label, value, bg, text }) {
  return (
    <div
      className="flex flex-col gap-1 p-4"
      style={{
        background: bg || "var(--oh-bg-elevated)",
        border: "0.5px solid var(--oh-border)",
        borderRadius: "12px",
      }}
    >
      <span className="text-2xl font-medium" style={{ color: text || "var(--oh-text)" }}>
        {value}
      </span>
      <span className="text-xs" style={{ color: text || "var(--oh-text-secondary)" }}>
        {label}
      </span>
    </div>
  );
}

export default function GouvFindings() {
  const [stats, setStats] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = useCallback(
    () => ({ ...getTenantHeaders(), "Content-Type": "application/json" }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, listRes] = await Promise.all([
        fetch(`${API}/api/governance/findings/stats`, {
          headers: headers(),
          credentials: "include",
        }),
        fetch(`${API}/api/governance/findings?limit=50`, {
          headers: headers(),
          credentials: "include",
        }),
      ]);
      if (!statsRes.ok) throw new Error(`stats HTTP ${statsRes.status}`);
      if (!listRes.ok) throw new Error(`findings HTTP ${listRes.status}`);
      const statsData = await statsRes.json();
      const listData = await listRes.json();
      setStats(statsData || null);
      setFindings(Array.isArray(listData?.findings) ? listData.findings : []);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
      setStats(null);
      setFindings([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const parGravite = stats?.par_gravite || {};
  const parStatut = stats?.par_statut || {};
  const total = stats?.total ?? 0;

  return (
    <div className="space-y-6" style={{ color: "var(--oh-text)" }}>
      {/* En-tête */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "var(--oh-stat-orange-bg)" }}
          >
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--oh-stat-orange-text)" }} />
          </div>
          <div>
            <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>
              Findings gouvernance
            </h2>
            <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              Collection canonique governance_findings
              {stats?.scope ? ` · scope ${stats.scope}` : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="Rafraîchir les findings"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm"
          style={{
            border: "0.5px solid var(--oh-border)",
            color: "var(--oh-text-secondary)",
            borderRadius: "8px",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </button>
      </div>

      {/* Erreur non-cassante */}
      {error && (
        <div
          className="p-4 text-sm"
          style={{
            background: "var(--oh-stat-red-bg)",
            color: "var(--oh-stat-red-text)",
            borderRadius: "8px",
          }}
        >
          Impossible de charger les findings : {error}
        </div>
      )}

      {/* Compteurs par gravité (issus de /stats) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total" value={total} />
        {GRAVITE_ORDER.map((g) => {
          const meta = graviteMeta(g);
          return (
            <StatTile
              key={g}
              label={meta.label}
              value={parGravite[g] ?? 0}
              bg={meta.bg}
              text={meta.text}
            />
          );
        })}
      </div>

      {/* Ventilation par statut (issue de /stats) */}
      {Object.keys(parStatut).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(parStatut).map(([statut, count]) => (
            <span
              key={statut}
              className="inline-flex items-center gap-2 px-3 py-1 text-xs"
              style={{
                background: "var(--oh-bg-elevated)",
                border: "0.5px solid var(--oh-border)",
                color: "var(--oh-text-secondary)",
                borderRadius: "8px",
              }}
            >
              <span style={{ color: "var(--oh-text)" }}>{statut}</span>
              <span className="font-medium" style={{ color: "var(--oh-text)" }}>
                {count}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Liste des findings (issue de /findings?limit=50) */}
      <div
        style={{
          background: "var(--oh-bg-elevated)",
          border: "0.5px solid var(--oh-border)",
          borderRadius: "12px",
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: "0.5px solid var(--oh-border)" }}
        >
          <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
            Derniers findings
          </span>
          <span className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
            {findings.length} affiché{findings.length > 1 ? "s" : ""}
          </span>
        </div>

        {/* État de chargement */}
        {loading && (
          <div className="p-8 text-center text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            Chargement des findings…
          </div>
        )}

        {/* État vide non-cassant */}
        {!loading && findings.length === 0 && !error && (
          <div className="p-8 text-center text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            Aucun finding à afficher.
          </div>
        )}

        {/* Table lecture seule */}
        {!loading && findings.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "var(--oh-text-secondary)" }}>
                  <th className="text-left font-medium px-4 py-2">Référence</th>
                  <th className="text-left font-medium px-4 py-2">Gravité</th>
                  <th className="text-left font-medium px-4 py-2">Statut</th>
                  <th className="text-left font-medium px-4 py-2">Tenant</th>
                  <th className="text-left font-medium px-4 py-2">Titre</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((f, i) => (
                  <tr
                    key={f.reference || i}
                    style={{ borderTop: "0.5px solid var(--oh-border)" }}
                  >
                    <td className="px-4 py-2 whitespace-nowrap" style={{ color: "var(--oh-text)" }}>
                      {f.reference || "—"}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <GraviteBadge gravite={f.gravite} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <StatutBadge statut={f.statut} />
                    </td>
                    <td
                      className="px-4 py-2 whitespace-nowrap"
                      style={{ color: "var(--oh-text-secondary)" }}
                    >
                      {f.tenant_id || "—"}
                    </td>
                    <td className="px-4 py-2" style={{ color: "var(--oh-text)" }}>
                      {f.title || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
